// NetPosition.jsx
import React, { useEffect, useState, useCallback } from 'react';
import './NetPosition.css';
import { API_BASE_URL } from '../config';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

// Supported indices (must match NFO instruments "name" used by backend)
const INDEX_OPTIONS = [
  { label: 'NIFTY', value: 'NIFTY' },
  { label: 'BANKNIFTY', value: 'BANKNIFTY' },
  { label: 'FINNIFTY', value: 'FINNIFTY' },
  { label: 'MIDCPNIFTY', value: 'MIDCPNIFTY' },
];

const NetPosition = React.memo(({ index = 'NIFTY' }) => {
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchPCR = useCallback(async (idx) => {
    setLoading(true);
    setError('');
    try {
      const oiRes = await fetch(`${API_BASE_URL}/oi?index=${encodeURIComponent(idx)}`);
      if (!oiRes.ok) {
        throw new Error(`HTTP ${oiRes.status}`);
      }
      const oiJson = await oiRes.json();

      if (!oiJson.success) {
        throw new Error(oiJson.message || 'Request failed');
      }

      const toNumber = (value) => {
        if (value == null) return 0;
        if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
        if (typeof value === 'string') {
          const num = parseFloat(value.replace(/,/g, ''));
          return Number.isFinite(num) ? num : 0;
        }
        const num = Number(value);
        return Number.isFinite(num) ? num : 0;
      };

      const summary = oiJson.summary || {};
      const payload = oiJson.data || {};
      
      // Try to get OI change values first from summary
      const callOiChange = toNumber(
        summary.call_oi_change ?? 
        summary.total_call_oi_change ?? 
        oiJson.total_call_oi_change_lots ?? 
        payload.total_call_oi_change_lots
      );
      const putOiChange = toNumber(
        summary.put_oi_change ?? 
        summary.total_put_oi_change ?? 
        oiJson.total_put_oi_change_lots ?? 
        payload.total_put_oi_change_lots
      );
      
      // Get total OI values from summary
      const callOi = toNumber(
        summary.call_oi ?? 
        summary.total_call_oi ?? 
        oiJson.total_call_oi_lots ?? 
        payload.total_call_oi_lots ?? 
        payload.total_call_oi
      );
      const putOi = toNumber(
        summary.put_oi ?? 
        summary.total_put_oi ?? 
        oiJson.total_put_oi_lots ?? 
        payload.total_put_oi_lots ?? 
        payload.total_put_oi
      );

      const callValue = callOiChange !== 0 || putOiChange !== 0 ? callOiChange : callOi;
      const putValue = callOiChange !== 0 || putOiChange !== 0 ? putOiChange : putOi;

      setChartData({
        labels: ['Open Interest'],
        datasets: [
          {
            label: callOiChange !== 0 || putOiChange !== 0 ? 'Call OI Change (Lots)' : 'Total Call OI (Lots)',
            data: [callValue],
            backgroundColor: '#ef4444',
          },
          {
            label: callOiChange !== 0 || putOiChange !== 0 ? 'Put OI Change (Lots)' : 'Total Put OI (Lots)',
            data: [putValue],
            backgroundColor: '#22c55e',
          },
        ],
      });
    } catch (err) {
      setError(err.message || 'Failed to fetch data');
      setChartData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPCR(index);
  }, [index, fetchPCR]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: { mode: 'nearest', intersect: false },
    },
    scales: {
      x: { grid: { display: false } },
      y: { grid: { color: 'rgba(0,0,0,0.08)' } },
    },
  };

  return (
    <div className="net-position-container">
      <div className="search-bar">
        <select
          className="index-select"
          value={index}
          onChange={() => {}}
          disabled
          title="Select Index"
        >
          {INDEX_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <button className="go-btn" onClick={() => fetchPCR(index)} disabled={loading}>
          {loading ? 'Loading...' : 'GO'}
        </button>
      </div>

      <h2 className="chart-title">Net Position</h2>

      <div className="net-position-chart">
        {error ? (
          <p className="error-text">{error}</p>
        ) : chartData ? (
          <Bar data={chartData} options={options} />
        ) : (
          <p>Loading...</p>
        )}
      </div>
    </div>
  );
});

export default NetPosition;
